import "./conciergeBackgroundRuntime";
import "./concierge-memory-runtime";
import { AdminAccess } from "./AdminAccess";
import { AdminErrorBoundary } from "./AdminErrorBoundary";
import { AdminWorkspaceAccess } from "./AdminWorkspace";
import { AdminWorkspaceSwitcher } from "./AdminWorkspaceSwitcher";
import { AppointmentEntry } from "./AppointmentEntry";
import { AppointmentProposalPage } from "./AppointmentProposalPage";
import { BookingContextIntro } from "./BookingContextIntro";
import { BookingLandingHeroV5 } from "./BookingLandingHeroV5";
import { ConciergeBooking } from "./ConciergeBooking";
import { parseAppRoute } from "./domain/appRoute";
import { EventAdminWorkspaceAccess } from "./EventAdminWorkspace";
import { DatenschutzPage, ImpressumPage } from "./LegalPages";
import {
  bookingPersonRequest,
  sharedAdminUrl,
  shouldKeepPersonalBookingOffJointHost,
  shouldOpenJointEventsAtRoot,
  shouldOpenPractitionerBookingAtRoot,
  shouldRedirectAdminToSharedHost,
} from "./lib/bookingHost";
import { bookingPersonPresentation } from "./lib/bookingPersonPresentation";
import { ManageBooking } from "./ManageBooking";
import { MeetingLinkNotice } from "./MeetingLinkNotice";
import { ProposalAdminWorkspaceAccess } from "./ProposalAdminWorkspace";
import { PublicEvents } from "./PublicEvents";
import { SiteFooter } from "./SiteFooter";
import { SystemHealthWorkspaceAccess } from "./SystemHealthWorkspace";
import "./admin-workspace-mobile.css";
import "./booking-flow-polish.css";
import "./mobile-booking-fixes.css";
import "./booking-selection-state.css";
import "./meeting-link-notice.css";
import "./appointment-proposal-final.css";
import "./concierge-memory.css";
import "./event-admin-operations.css";
import "./public-v5.css";
import "./public-v5-booking-flow.css";
import "./public-v5-quiet-pass.css";

export function App() {
  const route = parseAppRoute(window.location.search);

  if (route.kind === "admin") {
    if (shouldRedirectAdminToSharedHost()) {
      window.location.replace(sharedAdminUrl());
      return null;
    }

    return (
      <AdminErrorBoundary>
        <AdminWorkspaceSwitcher active={route.workspace} />
        {route.workspace === "events" && <EventAdminWorkspaceAccess />}
        {route.workspace === "bookings" && <AdminWorkspaceAccess />}
        {route.workspace === "health" && <SystemHealthWorkspaceAccess />}
        {route.workspace === "proposals" && <ProposalAdminWorkspaceAccess />}
        {route.workspace === "legacy" && <AdminAccess />}
      </AdminErrorBoundary>
    );
  }

  if (route.kind === "proposal") {
    return (
      <div className="public-v5">
        <AppointmentProposalPage token={route.token} />
        <SiteFooter />
      </div>
    );
  }

  if (route.kind === "manage") {
    return (
      <div className="public-v5">
        <ManageBooking reference={route.reference} managementToken={route.managementToken} />
        <SiteFooter />
      </div>
    );
  }

  const path = normalizePublicPath(window.location.pathname);
  const bookingRequest = bookingPersonRequest();
  const requestedPerson = bookingRequest.slug;
  const requestedPersonQuery = bookingRequest.fixedByHost ? null : requestedPerson;
  const bookingPerson = bookingPersonPresentation(requestedPerson);

  if (shouldKeepPersonalBookingOffJointHost(path)) {
    window.location.replace(new URL("/veranstaltungen", window.location.origin));
    return null;
  }

  if (path === "/impressum") {
    return <ImpressumPage />;
  }

  if (path === "/datenschutz") {
    return <DatenschutzPage />;
  }

  if (path === "/termine") {
    return (
      <div className="public-v5">
        <BackToStart />
        <AppointmentEntry />
        <SiteFooter />
      </div>
    );
  }

  if (path === "/online") {
    return (
      <div className="public-v5">
        <BackToAppointments requestedPerson={requestedPersonQuery} />
        <BookingContextIntro mode="online" bookingPerson={bookingPerson} />
        <div id="booking-flow">
          <ConciergeBooking bookingPerson={bookingPerson} />
        </div>
        <MeetingLinkNotice />
        <SiteFooter />
      </div>
    );
  }

  if (path === "/berlin") {
    if (bookingPerson.slug === "katrin") {
      const onlineUrl = new URL("/online", window.location.origin);
      onlineUrl.search = window.location.search;
      window.location.replace(onlineUrl);
      return null;
    }

    return (
      <div className="public-v5">
        <BackToAppointments requestedPerson={requestedPersonQuery} />
        <BookingContextIntro mode="berlin" bookingPerson={bookingPerson} />
        <div id="booking-flow">
          <ConciergeBooking bookingPerson={bookingPerson} />
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (path === "/veranstaltungen" || path.startsWith("/veranstaltungen/")) {
    const eventSlug = path.startsWith("/veranstaltungen/")
      ? decodeURIComponent(path.slice("/veranstaltungen/".length))
      : undefined;
    return (
      <div className="public-v5">
        <BackToStart />
        <PublicEvents eventSlug={eventSlug} />
        <SiteFooter />
      </div>
    );
  }

  const section = new URLSearchParams(window.location.search).get("bereich");

  if (section === "termine") {
    return (
      <div className="public-v5">
        <BackToStart />
        <AppointmentEntry />
        <SiteFooter />
      </div>
    );
  }

  if (section === "veranstaltungen") {
    return (
      <div className="public-v5">
        <BackToStart />
        <PublicEvents />
        <SiteFooter />
      </div>
    );
  }

  if (path === "/" && !section && shouldOpenJointEventsAtRoot()) {
    return (
      <div className="public-v5">
        <PublicEvents />
        <SiteFooter />
      </div>
    );
  }

  if (path === "/" && !section && shouldOpenPractitionerBookingAtRoot()) {
    return (
      <div className="public-v5">
        <AppointmentEntry />
        <SiteFooter />
      </div>
    );
  }

  return <BookingLandingHeroV5 />;
}

function normalizePublicPath(pathname: string) {
  const normalized = pathname.replace(/\/+$/, "");
  return normalized || "/";
}

function BackToStart() {
  return <BackButton label="← Übersicht" target="/" />;
}

function BackToAppointments({ requestedPerson }: { requestedPerson: string | null }) {
  const target = requestedPerson
    ? `/termine?person=${encodeURIComponent(requestedPerson)}`
    : "/termine";
  return <BackButton label="← Termine" target={target} />;
}

function BackButton({ label, target }: { label: string; target: string }) {
  function openTarget() {
    window.location.assign(new URL(target, window.location.origin));
  }

  return (
    <button className="public-v5__back" type="button" onClick={openTarget}>
      {label}
    </button>
  );
}
